import { LockOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  GrantedRequestStatuses,
  TerminalRequestStatuses,
} from "@p0security/shared/types/request-status";
import {
  Button,
  Flex,
  Input,
  Popconfirm,
  Table,
  Tooltip,
  Typography,
} from "antd";
import { formatDistance, parseISO } from "date-fns";
import { UserManager } from "oidc-client-ts";
import { useCallback, useMemo, useState } from "react";

import { useIntegration } from "../../hooks/useIntegration";
import { AuthFetch, useAuthFetch } from "../Login/hook.ts";
import { OIDC_POPUP_SETTINGS_KEY } from "../Login/routes/CustomOidcScreens.tsx";

const age = (value: string) => formatDistance(parseISO(value), Date.now());

const readAccess = "roles/secretmanager.viewer";
const writeAccess = "roles/secretmanager.secretVersionManager";

// Representative permission for each role, used with testIamPermissions
const rolePermission: Record<string, string> = {
  [readAccess]: "secretmanager.versions.access",
  [writeAccess]: "secretmanager.versions.add",
};

const POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const POLL_INTERVAL_MS = 5000;
const grantedStatuses = new Set<string>(GrantedRequestStatuses);
const terminalStatuses = new Set<string>(TerminalRequestStatuses);

export const pollUntilGranted = async (
  requestId: string,
  secretName: string,
  authFetch: AuthFetch
): Promise<void> => {
  const deadline = Date.now() + POLL_TIMEOUT_MS;

  while (true) {
    if (Date.now() > deadline) {
      throw new Error(
        `Timed out waiting for P0 request '${requestId}' to be granted for secret '${secretName}'`
      );
    }

    const response = await authFetch(`permission-requests/${requestId}`, {
      method: "GET",
    });
    if (!response?.ok) {
      throw new Error(
        `P0 permission-requests poll failed for request '${requestId}'`
      );
    }

    const { status } = await response.json();

    if (grantedStatuses.has(status)) return;
    if (terminalStatuses.has(status)) {
      throw new Error(
        `P0 request '${requestId}' for secret '${secretName}' ended with status: ${status}`
      );
    }

    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }
};

const RequestAccessButton: React.FC<{
  secretName: string;
  role: string;
  label: string;
  granted: boolean | undefined;
}> = ({ secretName, role, label, granted }) => {
  const authFetch = useAuthFetch();
  const [loading, setLoading] = useState(false);
  const [requested, setRequested] = useState(false);
  const [localGranted, setLocalGranted] = useState(false);

  const effectiveGranted = localGranted || granted;

  const handleRequestAccess = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authFetch("command", {
        method: "POST",
        json: {
          argv: [
            "request",
            "gcloud",
            "resource",
            "--service",
            "secretmanager",
            secretName,
            role,
            "--reason",
            "Request via p0 vault UI",
            "--principal-type",
            "wif-user",
          ],
          scriptName: "p0",
        },
      });
      const data = await response?.json();
      const requestId = data?.id as string | undefined;
      setRequested(true);
      if (requestId) {
        pollUntilGranted(requestId, secretName, authFetch)
          .then(() => setLocalGranted(true))
          .catch(() => {});
      }
    } finally {
      setLoading(false);
    }
  }, [authFetch, secretName, role]);

  const grantedLabel =
    label.replace(/^Request /, "").replace(/^\w/, (c) => c.toUpperCase()) +
    " granted";

  return (
    <Button
      loading={loading}
      disabled={effectiveGranted !== false || requested}
      onClick={handleRequestAccess}
    >
      {effectiveGranted ? grantedLabel : requested ? "Access requested" : label}
    </Button>
  );
};

const exchangeOidcForWifToken = async (
  idToken: string,
  projectId: string,
  pool: string,
  provider: string
): Promise<string> => {
  const response = await fetch("https://sts.googleapis.com/v1/token", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
      audience: `${pool}/providers/${provider}`,
      subject_token_type: "urn:ietf:params:oauth:token-type:id_token",
      subject_token: idToken,
      requested_token_type: "urn:ietf:params:oauth:token-type:access_token",
      scope: "https://www.googleapis.com/auth/cloud-platform",
    }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`WIF token exchange failed: ${text}`);
  }
  const { access_token } = await response.json();
  return access_token as string;
};

type SecretVersion = {
  name: string;
  createTime: string;
  state: "DESTROYED" | "DISABLED" | "ENABLED";
};

const versionNumber = (name: string) => name.split("/").pop()!;

class ForbiddenError extends Error {}

const secretManagerFetch = async (
  wifToken: string,
  path: string,
  options?: RequestInit
): Promise<Response> => {
  const response = await fetch(
    `https://secretmanager.googleapis.com/v1/${path}`,
    {
      ...options,
      headers: {
        Authorization: `Bearer ${wifToken}`,
        "Content-Type": "application/json",
        ...options?.headers,
      },
    }
  );
  if (!response.ok) {
    const text = await response.text();
    if (response.status === 403) throw new ForbiddenError(text);
    throw new Error(text);
  }
  return response;
};

const listSecretVersions = async (
  wifToken: string,
  projectId: string,
  secretName: string
): Promise<SecretVersion[]> => {
  const response = await secretManagerFetch(
    wifToken,
    `projects/${projectId}/secrets/${secretName}/versions`
  );
  const { versions } = await response.json();
  return (versions ?? []) as SecretVersion[];
};

const setSecretVersionState = async (
  wifToken: string,
  versionName: string,
  action: "destroy" | "disable" | "enable"
): Promise<void> => {
  await secretManagerFetch(wifToken, `${versionName}:${action}`, {
    method: "POST",
  });
};

const addSecretVersion = async (
  wifToken: string,
  projectId: string,
  secretName: string,
  value: string
): Promise<void> => {
  await secretManagerFetch(
    wifToken,
    `projects/${projectId}/secrets/${secretName}:addVersion`,
    { method: "POST", body: JSON.stringify({ payload: { data: btoa(value) } }) }
  );
};

const testIamPermissions = async (
  wifToken: string,
  projectId: string,
  secretName: string,
  permissions: string[]
): Promise<string[]> => {
  const response = await secretManagerFetch(
    wifToken,
    `projects/${projectId}/secrets/${secretName}:testIamPermissions`,
    { method: "POST", body: JSON.stringify({ permissions }) }
  );
  const { permissions: granted } = await response.json();
  return (granted ?? []) as string[];
};

const readSecretVersion = async (
  wifToken: string,
  versionName: string
): Promise<string> => {
  const response = await secretManagerFetch(wifToken, `${versionName}:access`);
  const { payload } = await response.json();
  return atob(payload.data as string);
};

const OidcSignInButton: React.FC<{
  authority: string;
  clientId: string;
  projectId: string;
  pool: string;
  provider: string;
  secretName: string;
}> = ({ authority, clientId, projectId, pool, provider, secretName }) => {
  const [loading, setLoading] = useState(false);
  const [writeLoading, setWriteLoading] = useState(false);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [email, setEmail] = useState<string | undefined>();
  const [wifToken, setWifToken] = useState<string | undefined>();
  const [versions, setVersions] = useState<SecretVersion[]>([]);
  const [revealedValues, setRevealedValues] = useState<Record<string, string>>(
    {}
  );
  const [newValue, setNewValue] = useState("");
  const [versionsForbidden, setVersionsForbidden] = useState(false);
  const [grantedPermissions, setGrantedPermissions] = useState<
    string[] | undefined
  >(undefined);
  const [error, setError] = useState<string | undefined>();

  const hasPermission = (role: string) =>
    grantedPermissions?.includes(rolePermission[role]);

  const loadVersions = useCallback(
    async (token: string) => {
      setVersionsLoading(true);
      setVersionsForbidden(false);
      try {
        const result = await listSecretVersions(token, projectId, secretName);
        setVersions(result);
      } catch (err) {
        if (err instanceof ForbiddenError) {
          setVersionsForbidden(true);
          setVersions([]);
        } else {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        setVersionsLoading(false);
      }
    },
    [projectId, secretName]
  );

  const refresh = useCallback(
    async (token: string) => {
      const [granted] = await Promise.all([
        testIamPermissions(
          token,
          projectId,
          secretName,
          Object.values(rolePermission)
        ),
        loadVersions(token),
      ]);
      setGrantedPermissions(granted);
    },
    [projectId, secretName, loadVersions]
  );

  const handleSignIn = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const manager = new UserManager({
        authority,
        client_id: clientId,
        redirect_uri: `${window.location.origin}/oidc/popup-callback`,
        scope: "openid profile email",
      });
      sessionStorage.setItem(
        OIDC_POPUP_SETTINGS_KEY,
        JSON.stringify({ authority, clientId })
      );
      const user = await manager.signinPopup();
      setEmail(user.profile.email);
      const token = await exchangeOidcForWifToken(
        user.id_token!,
        projectId,
        pool,
        provider
      );
      setWifToken(token);
      await refresh(token);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [authority, clientId, projectId, pool, provider, refresh]);

  const handleReveal = useCallback(
    (versionName: string) => async () => {
      if (!wifToken) return;
      setError(undefined);
      try {
        const value = await readSecretVersion(wifToken, versionName);
        setRevealedValues((prev) => ({ ...prev, [versionName]: value }));
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [wifToken]
  );

  const handleVersionAction = useCallback(
    (versionName: string, action: "destroy" | "disable" | "enable") =>
      async () => {
        if (!wifToken) return;
        setError(undefined);
        try {
          await setSecretVersionState(wifToken, versionName, action);
          await loadVersions(wifToken);
        } catch (err) {
          setError(err instanceof Error ? err.message : String(err));
        }
      },
    [wifToken, loadVersions]
  );

  const handleRefresh = useCallback(() => {
    if (wifToken) void refresh(wifToken);
  }, [wifToken, refresh]);

  const handleNewValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setNewValue(e.target.value),
    []
  );

  const handleWriteSecret = useCallback(async () => {
    if (!wifToken) return;
    setWriteLoading(true);
    setError(undefined);
    try {
      await addSecretVersion(wifToken, projectId, secretName, newValue);
      setNewValue("");
      await loadVersions(wifToken);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setWriteLoading(false);
    }
  }, [wifToken, projectId, secretName, newValue, loadVersions]);

  const versionColumns = useMemo(() => {
    const canRead = (p: string[] | undefined) =>
      p?.includes(rolePermission[readAccess]);
    return [
      {
        title: "Version",
        dataIndex: "name",
        render: (name: string) => versionNumber(name),
        width: 80,
      },
      {
        title: "Created",
        dataIndex: "createTime",
        render: (t: string) => age(t),
        width: 120,
      },
      {
        title: "State",
        dataIndex: "state",
        width: 100,
      },
      {
        title: "Value",
        dataIndex: "name",
        key: "value",
        render: (name: string, row: SecretVersion) => {
          const readNotGranted = canRead(grantedPermissions) === false;
          return row.state === "ENABLED" ? (
            revealedValues[name] ? (
              <Typography.Text code copyable>
                {revealedValues[name]}
              </Typography.Text>
            ) : (
              <Tooltip
                title={readNotGranted ? "Requires read access" : undefined}
              >
                <Button
                  size="small"
                  disabled={readNotGranted}
                  onClick={handleReveal(name)}
                >
                  Reveal
                </Button>
              </Tooltip>
            )
          ) : null;
        },
      },
      {
        title: "Actions",
        dataIndex: "name",
        key: "actions",
        render: (name: string, row: SecretVersion) => {
          const writeNotGranted =
            grantedPermissions?.includes(rolePermission[writeAccess]) === false;
          return (
            <Flex gap="4px">
              {row.state === "ENABLED" && (
                <Popconfirm
                  title="Disable this version?"
                  onConfirm={handleVersionAction(name, "disable")}
                  disabled={writeNotGranted}
                >
                  <Tooltip
                    title={
                      writeNotGranted ? "Requires write access" : undefined
                    }
                  >
                    <span>
                      <Button size="small" disabled={writeNotGranted}>
                        Disable
                      </Button>
                    </span>
                  </Tooltip>
                </Popconfirm>
              )}
              {row.state === "DISABLED" && (
                <Button
                  size="small"
                  onClick={handleVersionAction(name, "enable")}
                >
                  Enable
                </Button>
              )}
              {row.state !== "DESTROYED" && (
                <Popconfirm
                  title="Permanently destroy this version?"
                  onConfirm={handleVersionAction(name, "destroy")}
                  disabled={writeNotGranted}
                >
                  <Tooltip
                    title={
                      writeNotGranted ? "Requires write access" : undefined
                    }
                  >
                    <span>
                      <Button size="small" danger disabled={writeNotGranted}>
                        Destroy
                      </Button>
                    </span>
                  </Tooltip>
                </Popconfirm>
              )}
            </Flex>
          );
        },
      },
    ];
  }, [grantedPermissions, revealedValues, handleReveal, handleVersionAction]);

  return (
    <Flex vertical gap="12px">
      <Flex align="center" gap="8px">
        {!email && (
          <Button loading={loading} onClick={handleSignIn}>
            Sign in with OIDC
          </Button>
        )}
        {email && (
          <Typography.Text type="secondary">
            Signed in as {email}
          </Typography.Text>
        )}
        {wifToken && (
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={versionsLoading}
            onClick={handleRefresh}
          />
        )}
      </Flex>
      {wifToken && (
        <>
          <Flex gap="8px">
            <RequestAccessButton
              secretName={secretName}
              role={readAccess}
              label="Request read access"
              granted={hasPermission(readAccess)}
            />
            <RequestAccessButton
              secretName={secretName}
              role={writeAccess}
              label="Request write access"
              granted={hasPermission(writeAccess)}
            />
          </Flex>
          <div>
            <Typography.Text type="secondary">Add new version</Typography.Text>
            <Flex gap="8px" style={{ marginTop: 4 }}>
              <Input.Password
                value={newValue}
                onChange={handleNewValueChange}
                placeholder="New secret value"
              />
              <Tooltip
                title={
                  hasPermission(writeAccess) === false
                    ? "Requires write access"
                    : undefined
                }
              >
                <Button
                  loading={writeLoading}
                  disabled={!newValue || hasPermission(writeAccess) === false}
                  onClick={handleWriteSecret}
                >
                  Write
                </Button>
              </Tooltip>
            </Flex>
          </div>
          <div style={{ position: "relative" }}>
            <Table<SecretVersion>
              columns={versionColumns}
              dataSource={versions}
              loading={versionsLoading}
              rowKey="name"
              size="small"
              pagination={false}
              locale={{
                emptyText: versionsForbidden ? (
                  <span>
                    <LockOutlined /> Requires list access
                  </span>
                ) : undefined,
              }}
            />
            {versionsForbidden && (
              <Flex
                align="center"
                justify="center"
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "rgba(0,0,0,0.2)",
                }}
              />
            )}
          </div>
        </>
      )}
      {error && <Typography.Text type="danger">{error}</Typography.Text>}
    </Flex>
  );
};

export const SecretEditor: React.FC<{
  secretName: string;
  projectId: string;
}> = ({ secretName, projectId }) => {
  const { doc } = useIntegration<{
    "iam-write"?: Record<
      string,
      {
        defaultPool?: string;
        defaultProvider?: string;
        clientId?: string;
        issuer?: string;
      }
    >;
  }>("gcloud");
  const wifConfig = Object.values(doc?.data?.["iam-write"] ?? {})[0];

  return (
    <Flex vertical gap="12px" style={{ marginTop: 16 }}>
      {wifConfig?.issuer &&
        wifConfig?.clientId &&
        wifConfig?.defaultPool &&
        wifConfig?.defaultProvider && (
          <OidcSignInButton
            authority={wifConfig.issuer}
            clientId={wifConfig.clientId}
            projectId={projectId}
            pool={wifConfig.defaultPool}
            provider={wifConfig.defaultProvider}
            secretName={secretName}
          />
        )}
    </Flex>
  );
};
